export type TransientJobConfig = {
  delta_time_i?: number;
  dynamic_hvac_start_point?: boolean;
  id?: number;
  percentile_to_consider?: number;
  recalculation_method?: string;
  room_function_variant?: string;
  scenarios: number[];
  t_air_exhaust?: number;
  time_interval_in_min?: number;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const parseOptionalNumber = (value: unknown) => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
};

const parseOptionalBoolean = (value: unknown) => {
  if (typeof value === "boolean") return value;
  return undefined;
};

const parseOptionalString = (value: unknown) => {
  if (typeof value === "string" && value.trim()) return value;
  return undefined;
};

const parseScenarioIds = (value: unknown): number[] => {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    if (typeof item === "number" && Number.isFinite(item)) return [item];
    if (typeof item === "string" && item.trim() !== "") {
      const parsed = Number(item);
      if (Number.isFinite(parsed)) return [parsed];
    }
    if (isRecord(item)) {
      const id = parseOptionalNumber(item.id);
      if (id != null) return [id];
    }
    return [];
  });
};

const unwrapConfigRecord = (value: unknown): null | Record<string, unknown> => {
  if (!isRecord(value)) return null;
  if (isRecord(value.data)) return value.data;
  if (isRecord(value.config)) return value.config;
  return value;
};

export const parseTrConfigResponse = (value: unknown): null | TransientJobConfig => {
  const row = unwrapConfigRecord(value);
  if (!row) return null;
  const config: TransientJobConfig = {
    delta_time_i: parseOptionalNumber(row.delta_time_i),
    dynamic_hvac_start_point: parseOptionalBoolean(row.dynamic_hvac_start_point),
    id: parseOptionalNumber(row.id),
    percentile_to_consider: parseOptionalNumber(row.percentile_to_consider),
    recalculation_method: parseOptionalString(row.recalculation_method),
    room_function_variant: parseOptionalString(row.room_function_variant),
    scenarios: parseScenarioIds(row.scenarios),
    t_air_exhaust: parseOptionalNumber(row.t_air_exhaust),
    time_interval_in_min: parseOptionalNumber(row.time_interval_in_min),
  };
  if (
    config.id == null &&
    config.delta_time_i == null &&
    config.percentile_to_consider == null &&
    config.t_air_exhaust == null &&
    config.time_interval_in_min == null &&
    config.dynamic_hvac_start_point == null &&
    !config.recalculation_method &&
    !config.room_function_variant &&
    config.scenarios.length === 0
  ) {
    return null;
  }
  return config;
};
